import fs from 'node:fs/promises';
import { setTimeout as delay } from 'node:timers/promises';

import type { LoreClient } from '../lib/api.ts';
import { BillingBlockedError } from '../lib/errors.ts';
import {
  describeFetchError,
  fetchWithRetry,
  isConnectPhaseFetchError,
  isMidFlightFetchError,
} from '../lib/fetchRetry.ts';
import { getWebBaseURL } from '../lib/web.ts';
import type { ScrubbedThreadFile } from '../scrubber/gate.ts';
import type { LocalThreadFile } from './harnesses/types.ts';

export const THREAD_POLL_INTERVAL_MS = 500;
export const THREAD_PARSING_TIMEOUT_MS = 2 * 60 * 1000;
export const UPLOAD_NETWORK_CALL_TIMEOUT_MS = 30_000;

export type UploadSessionArtifactFileInfo = {
  kind: 'upload' | 'output';
  file_name: string;
  presigned_url: string | null;
  storage_url: string;
};

export type UploadSessionFileInfo = {
  id: string;
  presigned_url: string | null;
  storage_url: string;
  expected_md5: string;
  harness: string;
  error: string | null;
  thread: { id?: string; transcript_parsing_state?: string; visibility?: string } | null;
  // Cowork only — presigned PUT targets for the session's artifact bytes,
  // parallel to (and independent of) the scrubbed transcript upload.
  artifact_files?: UploadSessionArtifactFileInfo[];
};

export type CompleteUploadSessionFileInfo = {
  storage_url: string;
  harness: string;
  thread: { id?: string; transcript_parsing_state?: string; visibility?: string } | null;
};

type NonEmptyArray<T> = [T, ...T[]];

type UploadNetworkCallOptions = {
  timeoutMs?: number;
};

export type CreateUploadSessionOptions = UploadNetworkCallOptions & {
  /**
   * Caller-owned upload metadata attached to every thread file in the
   * session (see `upload_metadata` in the contract). Used by Upload API key
   * integrations (e.g. the Lore SDK) to tag sessions with their own
   * identifiers; the CLI's own upload paths leave it unset.
   */
  uploadMetadata?: Record<string, string>;
};

export class UploadNetworkTimeoutError extends Error {
  constructor(action: string, timeoutMs: number) {
    super(`${action} timed out after ${timeoutMs}ms`);
    this.name = 'UploadNetworkTimeoutError';
  }
}

// Body shapes we treat as "this action is gated by billing". The Lore API
// returns either a plain `{ message }` payload or one tagged with a `code` —
// match both so the CLI can flag paywall errors with a useful next step
// regardless of which response shape the server is currently emitting.
const BILLING_BLOCKED_CODES: ReadonlySet<string> = new Set([
  'billing_required',
  'paywall',
  'plan_required',
  'upgrade_required',
]);

// Conservative keyword matcher for the fallback case where the API returns a
// 402/403 without a structured `code`. We only consult message text on
// payment-flavored statuses so an unrelated 403 ("Only the thread author can
// update this thread") can't accidentally render as a billing error.
const BILLING_BLOCKED_KEYWORDS = /\b(paywall|billing(?:\s+plan)?|upgrade\s+to)\b/i;

function readMessage(body: unknown): string {
  if (isRecord(body) && typeof body.message === 'string') return body.message;
  return '';
}

function readCode(body: unknown): string {
  if (isRecord(body) && typeof body.code === 'string') return body.code.trim().toLowerCase();
  return '';
}

export function isBillingBlockedResponse(status: number, body: unknown): boolean {
  if (status === 402) return true;
  const code = readCode(body);
  if (code && BILLING_BLOCKED_CODES.has(code)) return true;
  if (status === 403) {
    const message = readMessage(body);
    if (message && BILLING_BLOCKED_KEYWORDS.test(message)) return true;
  }
  return false;
}

// Build a thrown Error for a non-success API response. Detects billing-blocked
// shapes up front so callers don't have to repeat the check at every call
// site; the resulting `BillingBlockedError` carries the original server
// message plus a direct link to the billing page.
export function interpretApiError(
  action: string,
  status: number,
  body: unknown,
  options: { billingUrl?: string } = {},
): Error {
  if (isBillingBlockedResponse(status, body)) {
    const billingUrl =
      options.billingUrl ??
      `${getWebBaseURL().replace(/\/+$/, '')}/billing`;
    return new BillingBlockedError({
      action,
      serverMessage: readMessage(body),
      billingUrl,
    });
  }
  return new Error(apiErrorMessage(action, status, body));
}

export async function createUploadSession(
  client: LoreClient,
  // Only the scrub gate (gate.ts) can mint a ScrubbedThreadFile, so this
  // signature makes "upload without scrubbing" a compile error. (TAN-4341)
  localFiles: ScrubbedThreadFile[],
  options: CreateUploadSessionOptions = {},
): Promise<{
  id: string;
  thread_files: UploadSessionFileInfo[];
}> {
  const threadFiles = localFiles.map((localFile) => ({
    md5: localFile.md5,
    harness: localFile.harness,
    filename: localFile.filename,
    repo_origin_path: localFile.repoOriginPath || undefined,
    file_type: localFile.fileType,
    harness_internal_id: localFile.harnessInternalId,
    artifact_metadata: localFile.artifactMetadata,
    artifact_files: localFile.artifactFiles?.map((artifact) => ({
      kind: artifact.kind,
      file_name: artifact.fileName,
      md5: artifact.md5,
      size_in_bytes: artifact.sizeInBytes,
      content_type: artifact.contentType,
    })),
  })) as NonEmptyArray<{
    md5: string;
    harness: LocalThreadFile['harness'];
    filename: string;
    repo_origin_path?: string;
    file_type?: LocalThreadFile['fileType'];
    harness_internal_id: string;
    artifact_metadata?: { uploads: string[]; outputs: string[] };
    artifact_files?: Array<{
      kind: 'upload' | 'output';
      file_name: string;
      md5: string;
      size_in_bytes: number;
      content_type: string | null;
    }>;
  }>;
  const res = await withUploadNetworkTimeout(
    'create upload session',
    options.timeoutMs,
    async () => client.createUploadSession({
      body: {
        thread_files: threadFiles,
        ...(options.uploadMetadata && Object.keys(options.uploadMetadata).length > 0
          ? { upload_metadata: options.uploadMetadata }
          : {}),
      },
    }),
  );
  if (res.status !== 201) {
    throw interpretApiError('create upload session', res.status, res.body);
  }
  return res.body as { id: string; thread_files: UploadSessionFileInfo[] };
}

export type UploadSessionFileOptions = {
  // Injected for tests so retries don't burn wall clock; defaults to
  // node:timers/promises setTimeout.
  sleep?: (ms: number) => Promise<void>;
  timeoutMs?: number;
};

export class UploadSessionFileError extends Error {
  readonly status: number;

  constructor(status: number, detail: string) {
    super(`upload transcript: unexpected status ${status}: ${detail}`);
    this.name = 'UploadSessionFileError';
    this.status = status;
  }
}

// Retry connect-phase AND mid-flight failures for S3 uploads. S3 PUT
// requests with the same key are idempotent — re-uploading the same
// content to the same presigned URL produces the same result — so it's
// safe to retry ECONNRESET / ETIMEDOUT / EPIPE (Sentry LORE-CLI-21,
// LORE-CLI-23). Non-ok responses are surfaced as sanitized
// UploadSessionFileError instances so Sentry can group them without the
// raw S3 RequestId/HostId noise.
export async function uploadSessionFile(
  presignedURL: string,
  tempPath: string,
  options: UploadSessionFileOptions = {},
): Promise<void> {
  const bytes = await fs.readFile(tempPath);
  await fetchWithRetry(
    async () => {
      const res = await withUploadNetworkTimeout(
        'upload transcript',
        options.timeoutMs,
        async (signal) => fetch(presignedURL, {
          method: 'PUT',
          body: bytes,
          headers: {
            'content-length': String(bytes.length),
          },
          signal,
        }),
      );
      if (!res.ok) {
        throw await uploadSessionFileErrorFromResponse(res);
      }
    },
    {
      isRetryableError: (error) =>
        isConnectPhaseFetchError(error) || isMidFlightFetchError(error),
      describeRetryReason: (error) => `network:${describeFetchError(error)}`,
      logEvent: 'upload_session_file_retry',
      sleep: options.sleep,
    },
  );
}

// Artifact bytes use the same presigned-PUT transport as transcripts, but are a
// deliberately separate, NON-scrubbed path (arbitrary user/agent files — see the
// artifacts design). Keeping the raw PUT here, in the one transport-owning file,
// preserves the chokepoint invariant: `uploadSessionFile` stays the only
// scrubber-gated transcript path while artifacts ride this distinct entry point.
export async function uploadArtifactBytes(
  presignedURL: string,
  tempPath: string,
  options: UploadSessionFileOptions = {},
): Promise<void> {
  return uploadSessionFile(presignedURL, tempPath, options);
}

async function uploadSessionFileErrorFromResponse(
  res: Response,
): Promise<UploadSessionFileError> {
  const text = await res.text();
  const detail =
    (summarizeS3Error(text) ?? summarizeResponseBody(text) ?? res.statusText.trim()) ||
    'empty response body';
  return new UploadSessionFileError(res.status, detail);
}

function summarizeS3Error(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith('<')) return null;
  const code = extractXmlTag(trimmed, 'Code');
  if (!code) return null;
  const message = extractXmlTag(trimmed, 'Message');
  return `S3 ${code}${message ? `: ${message}` : ''}`;
}

function extractXmlTag(text: string, tag: string): string | null {
  const match = text.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`, 'i'));
  const value = match?.[1]?.trim();
  return value ? decodeBasicXmlEntities(value) : null;
}

function decodeBasicXmlEntities(text: string): string {
  return text
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
    .replaceAll('&quot;', '"')
    .replaceAll('&apos;', "'")
    .replaceAll('&amp;', '&');
}

function summarizeResponseBody(text: string): string | null {
  const summary = text.trim().replace(/\s+/g, ' ');
  if (!summary) return null;
  return summary.length > 500 ? `${summary.slice(0, 497)}...` : summary;
}

export type CompleteUploadSessionOptions = {
  sleep?: (ms: number) => Promise<void>;
  timeoutMs?: number;
};

// Completing an upload session is idempotent — calling it multiple times
// with the same session ID and file list has no additional effect — so we
// can safely retry mid-flight network errors (ECONNRESET, ETIMEDOUT, EPIPE).
// The ts-rest client already retries connect-phase errors; this wrapper
// adds mid-flight retry for this specific endpoint.
export async function completeUploadSession(
  client: LoreClient,
  uploadSessionID: string,
  threadFiles: UploadSessionFileInfo[],
  options: CompleteUploadSessionOptions = {},
): Promise<{ thread_files: CompleteUploadSessionFileInfo[] }> {
  const completeThreadFiles = threadFiles.map((threadFile) => ({
    storage_url: requireStorageURL(threadFile),
  })) as NonEmptyArray<{ storage_url: string }>;
  return fetchWithRetry(
    async () => {
      const res = await withUploadNetworkTimeout(
        'complete upload session',
        options.timeoutMs,
        async () => client.completeUploadSession({
          params: { id: uploadSessionID },
          body: {
            thread_files: completeThreadFiles,
          },
        }),
      );
      if (res.status !== 200) {
        throw interpretApiError('complete upload session', res.status, res.body);
      }
      return res.body as { thread_files: CompleteUploadSessionFileInfo[] };
    },
    {
      isRetryableError: isMidFlightFetchError,
      describeRetryReason: (error) => `network:${describeFetchError(error)}`,
      logEvent: 'complete_upload_session_retry',
      sleep: options.sleep,
    },
  );
}

export type WaitForThreadParsingOptions = {
  timeoutMs?: number;
  pollIntervalMs?: number;
  onPoll?: () => void | Promise<void>;
  signal?: AbortSignal;
};

// Poll the API until the thread reaches a terminal parsing state. The optional
// `onPoll` callback exists for the daemon path so the recorder can refresh
// its heartbeat while we wait; the foreground export path leaves it unset.
export async function waitForThreadParsing(
  client: LoreClient,
  threadID: string,
  options: WaitForThreadParsingOptions = {},
): Promise<void> {
  const timeoutMs = options.timeoutMs ?? THREAD_PARSING_TIMEOUT_MS;
  const pollIntervalMs = options.pollIntervalMs ?? THREAD_POLL_INTERVAL_MS;
  const startedAt = Date.now();
  for (;;) {
    if (options.signal?.aborted) throw new DOMException('The operation was aborted', 'AbortError');
    if (options.onPoll) await options.onPoll();
    const res = await client.getThreadParseStatus({ params: { id: threadID } });
    if (res.status !== 200) {
      // `completeUploadSession` returns after committing the pending thread row,
      // but production reads can lag briefly behind that write. Treat a 404 as
      // transient while the normal parse wait budget remains; other statuses
      // are real API errors and should still fail immediately.
      if (res.status === 404) {
        const elapsedMs = Date.now() - startedAt;
        if (elapsedMs < timeoutMs) {
          await delay(Math.min(pollIntervalMs, timeoutMs - elapsedMs), undefined, { signal: options.signal });
          continue;
        }
      }
      throw interpretApiError(`get thread ${threadID}`, res.status, res.body);
    }

    const state = res.body.transcript_parsing_state;
    if (state === 'parsed' || state === 'too_large_to_parse') return;
    if (state === 'failed') {
      throw new Error(`thread ${threadID} parsing failed permanently`);
    }
    if (state !== 'pending' && state !== 'parsing') {
      throw new Error(
        `unexpected transcript parsing state for thread ${threadID}: ${JSON.stringify(state)}`,
      );
    }

    const elapsedMs = Date.now() - startedAt;
    if (elapsedMs >= timeoutMs) {
      throw new Error(
        `timed out waiting for thread ${threadID} parsing after ${timeoutMs}ms`,
      );
    }

    await delay(Math.min(pollIntervalMs, timeoutMs - elapsedMs), undefined, { signal: options.signal });
  }
}

export function reorderLocalFilesByExpectedMD5(
  localFiles: LocalThreadFile[],
  uploadFiles: UploadSessionFileInfo[],
): LocalThreadFile[] {
  const localFilesByMD5 = new Map<string, LocalThreadFile[]>();
  for (const localFile of localFiles) {
    const key = localFile.md5.trim().toLowerCase();
    localFilesByMD5.set(key, [...(localFilesByMD5.get(key) ?? []), localFile]);
  }

  return uploadFiles.map((uploadFile, index) => {
    const expectedMD5 = uploadFile.expected_md5?.trim();
    if (!expectedMD5) {
      throw new Error(`upload session response missing expected_md5 for thread_files[${index}]`);
    }

    const normalizedExpectedMD5 = expectedMD5.toLowerCase();
    const candidates = localFilesByMD5.get(normalizedExpectedMD5);
    if (!candidates || candidates.length === 0) {
      throw new Error(
        `upload session response returned unexpected expected_md5 ${expectedMD5}`,
      );
    }

    const [candidate, ...remaining] = candidates;
    if (!candidate) {
      throw new Error(`upload session response returned unexpected expected_md5 ${expectedMD5}`);
    }
    if (remaining.length === 0) {
      localFilesByMD5.delete(normalizedExpectedMD5);
    } else {
      localFilesByMD5.set(normalizedExpectedMD5, remaining);
    }
    return candidate;
  });
}

// `createUploadSession` returns the canonical full storage URL
// (`s3://<bucket>/<key>` or `file:///abs/<key>`); we round-trip it
// verbatim so the server can match the request against the persisted
// thread_file row by storage key. Reconstructing the URL here used to
// double the `s3://<bucket>/` prefix and 400 with "Storage URL does not
// belong to upload session".
function requireStorageURL(threadFile: UploadSessionFileInfo): string {
  if (!threadFile.storage_url.trim()) {
    throw new Error('upload session response missing storage_url');
  }
  return threadFile.storage_url;
}

async function withUploadNetworkTimeout<T>(
  action: string,
  timeoutMs: number | undefined,
  operation: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const effectiveTimeoutMs = timeoutMs ?? UPLOAD_NETWORK_CALL_TIMEOUT_MS;
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new UploadNetworkTimeoutError(action, effectiveTimeoutMs));
    }, effectiveTimeoutMs);
    timer.unref?.();
  });

  try {
    return await Promise.race([operation(controller.signal), timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export function apiErrorMessage(action: string, status: number, body: unknown): string {
  if (isRecord(body) && typeof body.message === 'string') {
    return `${action} failed: HTTP ${status}: ${body.message}`;
  }
  return `${action} failed: HTTP ${status}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}
